import { rsosApi, baseQueryArgs, baseExtraOptions } from '../rsosApi';
import type {
  EventShare,
  EventShareApiResponse,
  CreateEventShareRequest,
  UpdateEventShareRequest,
} from './types';

/**
 * Extends the rsosApi for event share endpoints.
 * Authorization header is included via baseQueryArgs to tell RTK which token to use.
 */
export const eventShareApi = rsosApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    // GET - Fetch shares by phone number
    getEventSharesByPhone: builder.query<
      EventShare[],
      { org: string; phoneNumber: string }
    >({
      query: ({ org, phoneNumber }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}?uri=${phoneNumber}`,
      }),
      extraOptions: { ...baseExtraOptions },
      providesTags: (result, error, { org, phoneNumber }) => [
        // Normalize phoneNumber to match invalidation tag (remove tel:// prefix)
        {
          type: 'EventShare',
          id: `${org}-phone-${phoneNumber.replace('tel://', '')}`,
        },
      ],
    }),

    // GET - Fetch shares by session ID
    getEventSharesBySession: builder.query<
      EventShareApiResponse,
      { org: string; sessionId: string }
    >({
      query: ({ org, sessionId }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}?session_id=${sessionId}`,
      }),
      extraOptions: { ...baseExtraOptions },
      providesTags: (result, error, { org, sessionId }) => [
        { type: 'EventShare', id: `${org}-session-${sessionId}` },
      ],
    }),

    // POST - Create initial share
    createEventShare: builder.mutation<
      EventShare,
      { org: string } & CreateEventShareRequest
    >({
      query: ({ org, ...body }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}`,
        method: 'POST',
        body,
      }),
      extraOptions: { ...baseExtraOptions },
      invalidatesTags: (result, error, { org }) => {
        const tags: { type: 'EventShare'; id: string }[] = [];
        if (result?.uri) {
          tags.push({
            type: 'EventShare',
            id: `${org}-phone-${result.uri.replace('tel://', '')}`,
          });
        }
        if (result?.session_id) {
          tags.push({
            type: 'EventShare',
            id: `${org}-session-${result.session_id}`,
          });
        }
        return tags;
      },
    }),

    // PATCH - Update existing share using session ID
    updateEventShare: builder.mutation<
      EventShare,
      {
        org: string;
        sessionId: string;
        uri?: string;
      } & UpdateEventShareRequest
    >({
      query: ({ org, sessionId, ...body }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}/${sessionId}`,
        method: 'PATCH',
        body,
      }),
      extraOptions: { ...baseExtraOptions },
      invalidatesTags: (_result, _error, { org, sessionId, uri }) => {
        const tags: { type: 'EventShare'; id: string }[] = [
          { type: 'EventShare', id: `${org}-session-${sessionId}` },
        ];
        // Also invalidate phone-based query if uri is provided
        if (uri) {
          tags.push({
            type: 'EventShare',
            id: `${org}-phone-${uri.replace('tel://', '')}`,
          });
        }
        return tags;
      },
    }),

    // DELETE - Remove specific recipient by share_key
    deleteRecipient: builder.mutation<
      void,
      { org: string; sessionId: string; shareKey: string; uri?: string }
    >({
      query: ({ org, sessionId, shareKey }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}/${sessionId}/recipients/${shareKey}`,
        method: 'DELETE',
      }),
      extraOptions: { ...baseExtraOptions },
      invalidatesTags: (_result, _error, { org, sessionId, uri }) => {
        const tags: { type: 'EventShare'; id: string }[] = [
          { type: 'EventShare', id: `${org}-session-${sessionId}` },
        ];
        // Also invalidate phone-based query if uri is provided
        if (uri) {
          tags.push({
            type: 'EventShare',
            id: `${org}-phone-${uri.replace('tel://', '')}`,
          });
        }
        return tags;
      },
    }),

    // DELETE - Delete a share session
    deleteEventShare: builder.mutation<
      void,
      { org: string; sessionId: string; uri?: string }
    >({
      query: ({ org, sessionId }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}/${sessionId}`,
        method: 'DELETE',
      }),
      extraOptions: { ...baseExtraOptions },
      invalidatesTags: (_result, _error, { org, sessionId, uri }) => {
        const tags: { type: 'EventShare'; id: string }[] = [
          { type: 'EventShare', id: `${org}-session-${sessionId}` },
        ];
        // Also invalidate phone-based query if uri is provided
        if (uri) {
          tags.push({
            type: 'EventShare',
            id: `${org}-phone-${uri.replace('tel://', '')}`,
          });
        }
        return tags;
      },
    }),
  }),
});

export const {
  useGetEventSharesByPhoneQuery,
  useGetEventSharesBySessionQuery,
  useCreateEventShareMutation,
  useUpdateEventShareMutation,
  useDeleteRecipientMutation,
  useDeleteEventShareMutation,
} = eventShareApi;
